/**
 * Billing > Customers (Phase 21 / Task 21.7a): every hauler, operator and disposal tenant with
 * its billing switch and its billing plan (the latest subscription's rate card).
 * GET /v1/platform/billing/accounts. A customer nobody has set up still shows, "Not billed".
 */
import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { Pill, searchInputStyle, subText } from '../../ui/bits';
import { useBillingCustomers, useBillingToday, type CustomerRow } from './billing-hooks';
import {
  BILLING_STATUS_LABELS,
  PLAN_GAP_LABELS,
  billedWithoutPlan,
  subscriptionLine,
} from './billing-format';

const TYPE_LABELS: Readonly<Record<CustomerRow['tenantType'], string>> = {
  HAULER: 'Hauler',
  OPERATOR: 'Operator',
  DISPOSAL: 'Disposal',
};

export function BillingCustomersPage(): React.JSX.Element {
  const navigate = useNavigate();
  const { data, isLoading, isError } = useBillingCustomers();
  const today = useBillingToday();
  const [q, setQ] = React.useState('');

  if (isLoading) return <p style={subText}>Loading…</p>;
  if (isError || data === undefined) {
    return (
      <p role="alert" style={{ color: 'var(--color-danger)' }}>
        Could not load the billing customers.
      </p>
    );
  }

  const needle = q.trim().toLowerCase();
  const rows = data.customers.filter(
    (c) => needle === '' || c.tenantName.toLowerCase().includes(needle),
  );

  return (
    <div>
      <input
        aria-label="Search customers"
        placeholder="Search customers"
        value={q}
        onChange={(e): void => setQ(e.target.value)}
        style={{ ...searchInputStyle, marginBottom: '12px', maxWidth: '320px' }}
      />
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
        <thead>
          <tr style={{ textAlign: 'left', ...subText }}>
            <th style={{ padding: '6px 8px' }}>Customer</th>
            <th style={{ padding: '6px 8px' }}>Type</th>
            <th style={{ padding: '6px 8px' }}>Billing</th>
            <th style={{ padding: '6px 8px' }}>Billing plan</th>
            <th style={{ padding: '6px 8px' }}>Invoices go to</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((c) => (
            <tr
              key={c.tenantId}
              data-testid={`billing-customer-${c.tenantId}`}
              onClick={(): void => {
                void navigate(`/billing/customers/${c.tenantId}`);
              }}
              style={{ cursor: 'pointer', borderTop: '1px solid var(--color-border-primary)' }}
            >
              <td style={{ padding: '8px' }}>{c.tenantName}</td>
              <td style={{ padding: '8px' }}>{TYPE_LABELS[c.tenantType]}</td>
              <td style={{ padding: '8px' }}>
                <Pill
                  tone={c.billingStatus === 'ACTIVE' ? 'positive' : 'muted'}
                  label={BILLING_STATUS_LABELS[c.billingStatus]}
                />{' '}
                {(() => {
                  const gap = today === null ? null : billedWithoutPlan(c, today);
                  return gap === null ? null : <Pill tone="warning" label={PLAN_GAP_LABELS[gap]} />;
                })()}
              </td>
              <td style={{ padding: '8px' }}>{subscriptionLine(c.latestSubscription)}</td>
              <td style={{ padding: '8px', ...subText }}>
                {c.toEmails.length === 0 ? '—' : c.toEmails.join(', ')}
              </td>
            </tr>
          ))}
          {rows.length === 0 && (
            <tr>
              <td colSpan={5} style={{ padding: '12px 8px', ...subText }}>
                No customers match.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
